import useFavorite from 'hooks/useFavorite';
import React from 'react';
import { Calendar, Flame, Heart, MapPin } from 'lucide-react';
import colors from 'theme/colors';
import userStatus from 'theme/userStatus';
import { ui, eventWhen, eventPlace, eventPrice, PLACEHOLDER_EVENT } from 'theme/discover';
import { useHighlightedEventOptional } from 'context/events/HighlightedEvent';
import {
    CompactCard,
    CompactThumb,
    CompactBadge,
    CompactHeart,
    CompactBody,
    CompactCategory,
    CompactTitle,
    CompactMeta,
    CompactFooter,
} from 'components/commons/CompactCard';

/**
 * Cartão de evento no desenho do app (WUICompactCard): foto em cima, categoria,
 * título em duas linhas, data e local, preço fechando o cartão. Mora numa
 * faixa horizontal no celular e numa grade no desktop — quem define a largura
 * é o pai.
 */
export default function EventCardV2({
    data,
    withoutTags = false,
    onEventClick,
    expired = false,
}) {
    const { isFavorited, toggleFavorite } = useFavorite(data?.slug, data?.user_status === userStatus.FAVORITE, data);
    const { highlightedSlug, highlightedSource, highlightEvent, clearHighlight } = useHighlightedEventOptional();

    const cardRef = React.useRef(null);
    const isHighlighted = !!data?.slug && highlightedSlug === data.slug;

    // Quando o destaque nasce no mapa, traz o card correspondente para a vista.
    // Sem isso o pin acende mas o card pode estar fora da área visível da lista.
    React.useEffect(() => {
        if (isHighlighted && highlightedSource === 'map') {
            cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [isHighlighted, highlightedSource]);

    const eventHref = data?.slug ? `/evento/${data.slug}` : undefined;
    const category = !withoutTags ? data?.tags?.[0]?.name : null;
    const price = eventPrice(data);

    // O card abre o detalhe em modal, mas continua sendo um link de verdade: assim
    // ele recebe foco, abre por Enter e responde a Ctrl/Cmd/clique-do-meio abrindo
    // em nova aba — comportamento esperado num catálogo, onde se compara eventos
    // lado a lado. Só interceptamos o clique simples, sem modificadores.
    function handleClick(e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) {
            return;
        }

        e.preventDefault();
        onEventClick(data);
    }

    return (
        <CompactCard
            id='event_card'
            ref={cardRef}
            as={eventHref ? 'a' : 'div'}
            href={eventHref}
            onClick={handleClick}
            $expired={expired}
            $highlighted={isHighlighted}
            onMouseEnter={() => highlightEvent(data?.slug, 'list')}
            onMouseLeave={clearHighlight}
            onFocus={() => highlightEvent(data?.slug, 'list')}
            onBlur={clearHighlight}
        >
            <CompactThumb>
                <img
                    src={data?.images?.[0] || data?.path || PLACEHOLDER_EVENT}
                    alt={data?.name ? `Imagem do evento ${data.name}` : 'Imagem do evento'}
                    loading='lazy'
                    onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_EVENT; }}
                />

                {data?.trending && (
                    <CompactBadge>
                        <Flame size={12} />
                        Em alta
                    </CompactBadge>
                )}

                <CompactHeart
                    type='button'
                    aria-pressed={isFavorited}
                    aria-label={isFavorited
                        ? `Remover ${data?.name || 'evento'} dos favoritos`
                        : `Favoritar ${data?.name || 'evento'}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        toggleFavorite();
                    }}
                >
                    <Heart
                        size={15}
                        color={isFavorited ? colors.occasionalPink : ui.ink}
                        fill={isFavorited ? colors.occasionalPink : 'none'}
                    />
                </CompactHeart>
            </CompactThumb>

            <CompactBody>
                {!!category && <CompactCategory>{category}</CompactCategory>}

                <CompactTitle>{data?.name}</CompactTitle>

                <CompactMeta>
                    <Calendar size={12} />
                    <span>{eventWhen(data)}</span>
                </CompactMeta>

                <CompactMeta>
                    <MapPin size={12} />
                    <span>{eventPlace(data)}</span>
                </CompactMeta>

                {!!price && <CompactFooter>{price}</CompactFooter>}
            </CompactBody>
        </CompactCard>
    )
};
